import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Plus, X } from 'lucide-react'
import { api } from '../services/api'
import Button from './Button'
import Input from './Input'

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

export default function TranslationBanner({
  page,
  locale,
  languageName,
  onTranslationCreated,
}) {
  const [showModal, setShowModal] = useState(false)
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [error, setError] = useState('')
  const slugManuallyEdited = useRef(false)
  const queryClient = useQueryClient()

  const createTranslationMutation = useMutation({
    mutationFn: (data) =>
      api.post(`/pages/${page.id}/translations/${locale}`, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['page', page.id.toString(), locale],
      })
      setShowModal(false)
      setTitle('')
      setSlug('')
      setError('')
      slugManuallyEdited.current = false
      if (onTranslationCreated) {
        onTranslationCreated()
      }
    },
    onError: (err) => {
      const message =
        err.response?.data?.message || 'Failed to create translation'
      setError(message)
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!title.trim() || !slug.trim()) {
      setError('Title and slug are required')
      return
    }

    createTranslationMutation.mutate({
      title: title.trim(),
      slug: slug.trim(),
    })
  }

  return (
    <>
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700">
              No <strong>{languageName}</strong> translation exists for this
              page. Blocks are visible but read-only until you add a
              translation.
            </p>
          </div>
          <div className="ml-4">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowModal(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add {languageName} Translation
            </Button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add {languageName} Translation
              </h3>
              <button
                onClick={() => {
                  setShowModal(false)
                  setError('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    type="text"
                    value={title}
                    onChange={(e) => {
                      const value = e.target.value
                      setTitle(value)
                      if (!slugManuallyEdited.current) {
                        setSlug(slugify(value))
                      }
                    }}
                    placeholder="Enter page title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <Input
                    type="text"
                    value={slug}
                    onChange={(e) => {
                      slugManuallyEdited.current = true
                      setSlug(e.target.value)
                    }}
                    placeholder="enter-page-slug"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    URL-friendly identifier for this page
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false)
                    setError('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createTranslationMutation.isPending}
                >
                  {createTranslationMutation.isPending
                    ? 'Creating...'
                    : 'Create Translation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
